import { LIMITS } from './types';
import type { SendFn, SendResult } from './types';
import type { EventQueue, QueuedEvent } from './eventQueue';

const FAILED: SendResult = { ok: false, status: 0, enabled: true };

/**
 * Sends the queue in batches. One ordinary flush runs at a time, so a page_leave can
 * never overtake its page_view. A final flush (tab hidden, sign-out, hand-off) may run
 * alongside one; the server ignores replays, so a repeated event does no harm.
 * Never throws: a tracking failure must not reach the app.
 */
export class Flusher {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight = false;
  private fails = 0;
  private disabledUntil = 0;
  private readonly queue: EventQueue;
  private readonly send: SendFn;

  constructor(queue: EventQueue, send: SendFn) {
    this.queue = queue;
    this.send = send;
  }

  /** True while the server said tracking is off, or the user is signed out. */
  get disabled(): boolean {
    return Date.now() < this.disabledUntil;
  }

  /** Call after events were queued: flush now if the batch is big, otherwise in a few seconds. */
  schedule(): void {
    if (this.disabled || this.queue.size === 0) return;
    if (this.fails === 0 && this.queue.size >= LIMITS.flushAt) {
      void this.flush(false);
      return;
    }
    if (this.timer === null) {
      const wait =
        this.fails === 0
          ? LIMITS.flushMs
          : (LIMITS.backoffMs[Math.min(this.fails - 1, LIMITS.backoffMs.length - 1)] ??
            LIMITS.flushMs);
      this.timer = setTimeout(() => {
        this.timer = null;
        void this.flush(false);
      }, wait);
    }
  }

  /** `keepalive` lets the request outlive the page (tab closing, sign-out, hand-off). */
  async flush(keepalive: boolean): Promise<void> {
    this.disarm();
    if (this.disabled) return;
    this.queue.purgeStale(Date.now());
    if (this.queue.size === 0) return;
    if (!keepalive && this.inFlight) return;

    const batch = this.queue.takeBatch();
    if (!keepalive) this.inFlight = true;

    let result: SendResult;
    try {
      result = await this.send(
        batch.map((item) => item.event),
        { keepalive },
      );
    } catch {
      result = FAILED;
    }

    if (!keepalive) this.inFlight = false;
    this.handle(result, batch);
  }

  dispose(): void {
    this.disarm();
  }

  private handle(result: SendResult, batch: QueuedEvent[]): void {
    if (result.ok) {
      this.fails = 0;
      this.queue.remove(batch);
      if (!result.enabled) {
        this.pause();
        return;
      }
    } else if (result.status === 400) {
      // A batch the server calls malformed never becomes valid: drop it, do not retry.
      this.fails = 0;
      this.queue.remove(batch);
    } else if (result.status === 401) {
      // Signed out or session expired: nothing can be recorded for now.
      this.pause();
      return;
    } else {
      // Network error, 403 (stale CSRF token), 429, 5xx: keep the events and back off.
      this.fails += 1;
    }
    this.schedule();
  }

  private pause(): void {
    this.disabledUntil = Date.now() + LIMITS.pauseMs;
    this.fails = 0;
    this.queue.clear();
    this.disarm();
  }

  private disarm(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
